import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Loading } from '../../components/ui/Loading';
import { apiClient } from '../../services/api';

interface DashboardStats {
  totalMembers: number;
  activeMembers: number;
  pendingRequests: number;
  activePartners: number;
  totalBenefits: number;
}

export const AdminDashboardPage = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalMembers: 0,
    activeMembers: 0,
    pendingRequests: 0,
    activePartners: 0,
    totalBenefits: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardStats();
  }, []);

  const loadDashboardStats = async () => {
    try {
      setLoading(true);

      // Fetch all stats in parallel
      const [membersResponse, requestsResponse, partnersResponse, benefitsResponse] = await Promise.all([
        apiClient.get<{ data: any[]; total: number }>('/admin/members', { limit: 1 }),
        apiClient.get<{ data: any[]; total: number }>('/admin/service-requests', { status: 'REQUESTED', limit: 1 }),
        apiClient.get<any[]>('/admin/partners'),
        apiClient.get<any[]>('/admin/benefits')
      ]);

      // Count active members
      const allMembersResponse = await apiClient.get<{ data: any[]; total: number }>('/admin/members', { status: 'ACTIVE', limit: 1 });

      // Count active partners
      const activePartners = Array.isArray(partnersResponse)
        ? partnersResponse.filter((p: any) => p.active).length
        : 0;

      setStats({
        totalMembers: membersResponse.total || 0,
        activeMembers: allMembersResponse.total || 0,
        pendingRequests: requestsResponse.total || 0,
        activePartners: activePartners,
        totalBenefits: Array.isArray(benefitsResponse) ? benefitsResponse.length : 0
      });
    } catch (error: any) {
      console.error('Failed to load dashboard stats:', error);
      toast.error('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Dashboard Overview</h1>
          <p className="text-slate-500 mt-1">Welcome back, here's what's happening today.</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-sm font-medium border border-indigo-100">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Members"
          value={stats.totalMembers}
          subtitle={`${stats.activeMembers} Active Users`}
          icon="👥"
          color="indigo"
          trend="+12% from last month"
        />
        <StatsCard
          title="Pending Requests"
          value={stats.pendingRequests}
          subtitle="Requires Attention"
          icon="🔔"
          color="amber"
          alert={stats.pendingRequests > 0}
        />
        <StatsCard
          title="Active Partners"
          value={stats.activePartners}
          subtitle="Healthcare Providers"
          icon="🏥"
          color="emerald"
        />
        <StatsCard
          title="Total Benefits"
          value={stats.totalBenefits}
          subtitle="Active Perks"
          icon="🎁"
          color="purple"
        />
      </div>

      {/* Quick Actions Section */}
      <div>
        <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
          <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <ActionCard to="/admin/members" icon="👥" title="Manage Members" desc="View and manage all member accounts" />
          <ActionCard to="/admin/requests" icon="📋" title="Service Requests" desc="Handle member service requests" highlight={stats.pendingRequests > 0} />
          <ActionCard to="/admin/partners" icon="🏥" title="Manage Partners" desc="Add and manage healthcare partners" />
          <ActionCard to="/admin/benefits" icon="🎁" title="Manage Benefits" desc="Configure membership benefits" />
          <ActionCard to="/admin/payments" icon="💳" title="Payments" desc="View payment history and invoices" />
          <ActionCard to="/admin/analytics" icon="📈" title="Analytics" desc="View platform analytics and reports" />
        </div>
      </div>
    </div>
  );
};

// Sub-components for cleaner code
const StatsCard = ({ title, value, subtitle, icon, color, trend, alert }: any) => {
  const colorClasses: Record<string, string> = {
    indigo: 'bg-indigo-50 text-indigo-600 ring-indigo-100',
    amber: 'bg-amber-50 text-amber-600 ring-amber-100',
    emerald: 'bg-emerald-50 text-emerald-600 ring-emerald-100',
    purple: 'bg-purple-50 text-purple-600 ring-purple-100',
  };

  return (
    <div className={`bg-white p-6 rounded-2xl shadow-sm border border-slate-100 transition-all duration-300 hover:shadow-md hover:-translate-y-1 relative group overflow-hidden`}>
      {/* Background decoration */}
      <div className={`absolute -right-4 -top-4 w-24 h-24 rounded-full opacity-5 ${colorClasses[color].split(' ')[0].replace('bg-', 'bg-')}`}></div>

      <div className="flex justify-between items-start mb-4 relative z-10">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${colorClasses[color]} ring-1 shadow-sm`}>
          {icon}
        </div>
        {alert && (
          <span className="flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-3 w-3 rounded-full bg-amber-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500"></span>
          </span>
        )}
      </div>

      <div className="relative z-10">
        <h3 className="text-slate-500 font-medium text-sm">{title}</h3>
        <div className="text-3xl font-bold text-slate-800 mt-1 mb-1">{value}</div>
        <div className="flex items-center text-sm">
          <span className={alert ? "text-amber-600 font-medium" : "text-slate-400"}>
            {subtitle}
          </span>
        </div>
        {trend && <div className="mt-3 text-xs font-medium text-emerald-600 bg-emerald-50 inline-block px-2 py-0.5 rounded-full">{trend}</div>}
      </div>
    </div>
  )
}

const ActionCard = ({ to, icon, title, desc, highlight }: any) => (
  <Link
    to={to}
    className={`bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-lg hover:border-indigo-100 transition-all duration-300 group ${highlight ? 'ring-2 ring-amber-400 ring-offset-2' : ''}`}
  >
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center text-2xl group-hover:bg-indigo-600 group-hover:text-white transition-colors duration-300 shadow-inner">
        {icon}
      </div>
      <div>
        <h3 className="text-lg font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">{title}</h3>
        <p className="text-slate-500 text-sm mt-1 leading-relaxed">{desc}</p>
      </div>
    </div>
  </Link>
);
